import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import {
  accountMappingService,
  TemplateIncomeCategory,
  TBAccount,
  SaveMappingDto,
} from './AccountMappingService';
import styles from './AccountMapping.module.css';

export const AccountMapping: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();
  const entityId = selectedPartnership?.id;

  const [templateVersionId, setTemplateVersionId] = useState<number | null>(null);
  const [categories, setCategories] = useState<TemplateIncomeCategory[]>([]);
  const [accounts, setAccounts] = useState<TBAccount[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    setLoadError(null);
    accountMappingService
      .resolveTemplateVersionId(entityId, taxYear)
      .then(tvId => {
        setTemplateVersionId(tvId);
        return Promise.all([
          accountMappingService.getCategories(tvId),
          accountMappingService.getAccounts(entityId, tvId),
        ]);
      })
      .then(([cats, accts]) => {
        setCategories(Array.isArray(cats) ? cats : []);
        setAccounts(Array.isArray(accts) ? accts : []);
        const initial: Record<string, string> = {};
        for (const a of Array.isArray(accts) ? accts : []) {
          if (a.mappedCategoryId) initial[a.accountId] = a.mappedCategoryId;
        }
        setMappings(initial);
      })
      .catch((err: Error) => {
        console.error('[CalcEngine] Account mapping load failed:', err);
        setLoadError(err.message || 'Failed to load account mapping data');
      })
      .finally(() => setLoading(false));
  }, [entityId, taxYear]);

  const mappedCount = Object.keys(mappings).length;

  const handleSave = async () => {
    if (!entityId || templateVersionId === null) return;
    setSaving(true);
    try {
      const toSave: SaveMappingDto[] = Object.entries(mappings).map(
        ([accountId, incomeCategoryId]) => ({
          accountId,
          accountName: accounts.find(a => a.accountId === accountId)?.accountName ?? '',
          incomeCategoryId,
        })
      );
      await accountMappingService.saveMappings(entityId, templateVersionId, toSave);
      message.success('Mappings saved successfully');
    } catch {
      message.error('Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const initial: Record<string, string> = {};
    for (const a of accounts) {
      if (a.mappedCategoryId) initial[a.accountId] = a.mappedCategoryId;
    }
    setMappings(initial);
  };

  if (!selectedPartnership) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.emptyState}>Select a partnership to view account mappings.</div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>CoA Mapping</h1>
          <p className={styles.pageSub}>
            Map trial balance accounts to income categories for{' '}
            <strong style={{ color: 'var(--ce-text-primary)' }}>{selectedPartnership.name}</strong>{' '}
            — {taxYear}
          </p>
        </div>
      </div>

      {loadError && (
        <div
          style={{
            background: 'rgba(239,68,68,0.10)',
            border: '1px solid rgba(239,68,68,0.35)',
            color: '#ef4444',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {loadError}
        </div>
      )}

      {loading ? (
        <div className={styles.loadingState}>Loading accounts and categories…</div>
      ) : loadError ? null : (
        <>
          <div className={styles.panels}>
            {/* LEFT PANEL — Trial Balance Accounts */}
            <div className={styles.leftPanel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>Trial Balance Accounts</span>
                <span className={mappedCount === accounts.length && accounts.length > 0
                  ? styles.badgeComplete : styles.badgePartial}>
                  {mappedCount} of {accounts.length} mapped
                </span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Account ID</th>
                      <th>Account Name</th>
                      <th className={styles.amountCol}>Amount</th>
                      <th>Mapped To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(acct => {
                      const mapped = mappings[acct.accountId];
                      return (
                        <tr key={acct.accountId}>
                          <td className={styles.accountId}>{acct.accountId}</td>
                          <td>
                            <div className={styles.accountNameCell}>
                              <span className={mapped ? styles.dotMapped : styles.dotUnmapped} />
                              {acct.accountName}
                            </div>
                          </td>
                          <td className={styles.amountCol}>
                            {acct.amount.toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              maximumFractionDigits: 0,
                            })}
                          </td>
                          <td>
                            <select
                              className={styles.categorySelect}
                              value={mapped ?? ''}
                              onChange={e => {
                                const val = e.target.value;
                                setMappings(prev => {
                                  const next = { ...prev };
                                  if (val) next[acct.accountId] = val;
                                  else delete next[acct.accountId];
                                  return next;
                                });
                              }}
                            >
                              <option value="">— Select category —</option>
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.categoryName}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                    {accounts.length === 0 && (
                      <tr>
                        <td colSpan={4} className={styles.emptyRow}>
                          No trial balance accounts found. Upload a trial balance first.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* RIGHT PANEL — Income Categories reference */}
            <div className={styles.rightPanel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>Income Categories</span>
              </div>
              <div className={styles.categoryList}>
                {categories.map((cat, i) => (
                  <div key={cat.id} className={styles.categoryItem}>
                    <div className={styles.categoryNumber}>{i + 1}</div>
                    <div className={styles.categoryContent}>
                      <div className={styles.categoryName}>{cat.categoryName}</div>
                      {cat.description && (
                        <div className={styles.categoryDesc}>{cat.description}</div>
                      )}
                    </div>
                  </div>
                ))}
                {categories.length === 0 && (
                  <div className={styles.emptyRow} style={{ padding: '20px 16px' }}>
                    No income categories found. Assign a template to Income Allocation in the
                    Calculation Profile first.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.footerStatus}>
              {accounts.length === 0
                ? 'No accounts loaded'
                : mappedCount === accounts.length
                ? `✓ All ${accounts.length} accounts mapped`
                : `${mappedCount} of ${accounts.length} accounts mapped`}
            </div>
            <div className={styles.footerActions}>
              <button className={styles.btnReset} onClick={handleReset}>
                Reset
              </button>
              <button
                className={styles.btnSave}
                onClick={handleSave}
                disabled={saving || mappedCount === 0}
              >
                {saving ? 'Saving…' : 'Save Mappings'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
